import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Minus, Plus, Truck } from "lucide-react";
import { useProduct, useProducts } from "@/hooks/useProducts";
import { useStore } from "@/store/StoreContext";
import { WishlistButton } from "@/components/WishlistButton";
import { ProductCard } from "@/components/ProductCard";
import { formatIDR } from "@/lib/format";
import { waLink, waProductMessage } from "@/lib/whatsapp";

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: product } = useProduct(slug);
  const { data: all = [] } = useProducts();
  const { addToCart } = useStore();
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);

  if (!product) return <div className="min-h-[60vh]" data-testid="product-loading" />;

  const related = all.filter((p) => p.slug !== product.slug).slice(0, 4);
  const gallery = product.images?.length ? product.images : [];

  return (
    <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-10 lg:px-16" data-testid="product-detail-page">
      <nav className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-stone">
        <Link to="/" className="hover:text-ink">Beranda</Link>
        <ChevronRight size={12} />
        <Link to="/koleksi" className="hover:text-ink">Koleksi</Link>
        <ChevronRight size={12} />
        <span className="text-ink">{product.name}</span>
      </nav>

      <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="overflow-hidden bg-linen">
            <img
              src={gallery[active]}
              alt={product.name}
              className="aspect-square w-full object-cover"
              data-testid="product-main-image"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-4">
              {gallery.map((g, i) => (
                <button
                  key={g}
                  onClick={() => setActive(i)}
                  className={`h-20 w-20 overflow-hidden border transition-colors duration-300 ${
                    active === i ? "border-ink" : "border-transparent"
                  }`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 lg:pt-4">
          <p className="label-eyebrow text-sand">{product.collection_name}</p>
          <h1 className="mt-4 font-display text-4xl leading-[1.08] text-ink lg:text-5xl" data-testid="product-title">
            {product.name}
          </h1>
          <p className="mt-3 text-sm text-stone">{product.color}</p>
          <p className="mt-7 text-2xl font-medium tracking-wide text-ink" data-testid="product-detail-price">
            {formatIDR(product.price)}
          </p>

          <p className="mt-8 text-base leading-relaxed text-stone">{product.description}</p>

          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center border border-line">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-4 py-3 text-ink transition-colors duration-300 hover:bg-linen"
                aria-label="Kurangi"
                data-testid="qty-decrease"
              >
                <Minus size={14} />
              </button>
              <span className="w-12 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                className="px-4 py-3 text-ink transition-colors duration-300 hover:bg-linen"
                aria-label="Tambah"
                data-testid="qty-increase"
              >
                <Plus size={14} />
              </button>
            </div>
            <WishlistButton
              product={product}
              size={20}
              className="h-12 w-12 border border-line"
              testId="detail-wishlist-toggle"
            />
          </div>

          <button
            onClick={() => addToCart(product, qty)}
            className="mt-6 w-full bg-ink py-4 text-[11px] uppercase tracking-[0.22em] text-paper transition-colors duration-300 hover:bg-espresso"
            data-testid="detail-add-to-cart"
          >
            Tambah ke Daftar Pilihan
          </button>
          <p className="mt-3 text-center text-[11px] text-stone">
            Menambahkan ke daftar pilihan tidak mengikat — pesanan diproses lewat WhatsApp.
          </p>
          <a
            href={waLink(waProductMessage(product))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center border border-ink py-4 text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            data-testid="detail-whatsapp-order"
          >
            Pesan via WhatsApp
          </a>

          <div className="mt-10 space-y-6 border-t border-line pt-8">
            <div>
              <p className="label-eyebrow text-sand">Material</p>
              <ul className="mt-3 space-y-1 text-sm text-stone">
                {product.materials?.map((m) => (
                  <li key={m}>· {m}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="label-eyebrow text-sand">Ukuran</p>
              <p className="mt-3 text-sm text-stone">{product.size}</p>
            </div>
            <div className="flex items-start gap-3 bg-linen p-5">
              <Truck size={17} strokeWidth={1.4} className="mt-[2px] shrink-0 text-sand" />
              <p className="text-sm leading-relaxed text-stone">
                Ongkos kirim dan ketersediaan dikonfirmasi lewat WhatsApp. Pengerjaan dan pengemasan
                1–2 hari kerja.
              </p>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-28 border-t border-line pt-14" data-testid="related-section">
          <p className="label-eyebrow text-sand">Mungkin kamu suka</p>
          <h2 className="mt-4 font-display text-3xl text-ink md:text-4xl">Rangkaian lainnya</h2>
          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-16 lg:grid-cols-4 lg:gap-x-8">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
